import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Table, Modal, Spinner } from 'react-bootstrap';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';

interface Feedback {
  id: string;
  name: string;
  feedback: string;
  createdAt: string;
  user: {
    name: string;
    email: string;
  };
}

interface FeedbackFormData {
  name: string;
  feedback: string;
}

export const FeedbackPage: React.FC = () => {
  const { user } = useAuth();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [formData, setFormData] = useState<FeedbackFormData>({
    name: user?.name || '',
    feedback: '',
  });

  useEffect(() => {
    fetchMyFeedbacks();
  }, []);

  const fetchMyFeedbacks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/feedback/my');
      setFeedbacks(response.data.data || []);
    } catch (error: any) {
      // If endpoint doesn't exist yet, show empty array
      if (error.response?.status === 404) {
        setFeedbacks([]);
      } else {
        toast.error('Failed to load your feedbacks');
      }
    } finally {
      setLoading(false);
    }
  };

  const createFeedbackMutation = useMutation({
    mutationFn: async (data: FeedbackFormData) => {
      const response = await api.post('/feedback', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Feedback submitted successfully!');
      setShowModal(false);
      setFormData({ name: user?.name || '', feedback: '' });
      fetchMyFeedbacks();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to submit feedback');
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    createFeedbackMutation.mutate(formData);
  };

  const handleViewDetails = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container fluid className="py-3">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">My Feedback</h2>
          <p className="text-muted mb-0 small">View and manage your feedback submissions</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowModal(true)}
          className="d-flex align-items-center"
        >
          <i className="fas fa-plus me-2"></i>
          Submit Feedback
        </Button>
      </div>

      {/* Feedbacks List */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <i className="fas fa-comment-dots me-2 text-primary"></i>
            Your Feedback ({feedbacks.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {feedbacks.length > 0 ? (
            <Table hover responsive className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="border-0">Date</th>
                  <th className="border-0">Name</th>
                  <th className="border-0">Feedback</th>
                  <th className="border-0">Actions</th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((feedback) => (
                  <tr key={feedback.id}>
                    <td className="text-muted small">
                      {formatDate(feedback.createdAt)}
                    </td>
                    <td>
                      <strong>{feedback.name}</strong>
                    </td>
                    <td>
                      <div
                        className="text-truncate"
                        style={{ maxWidth: '400px' }}
                        title={feedback.feedback}
                      >
                        {feedback.feedback}
                      </div>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewDetails(feedback)}
                      >
                        <i className="fas fa-eye"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-comment-dots text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">No feedback submitted yet</h5>
              <p className="text-muted">Share your thoughts and suggestions using the button above</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Submit Feedback Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-comment-dots me-2"></i>
            Submit Feedback
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <p className="text-muted mb-4">
              <i className="fas fa-info-circle me-2"></i>
              We value your feedback and appreciate your time!
            </p>

            <Form.Group className="mb-3">
              <Form.Label>
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Feedback <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                name="feedback"
                value={formData.feedback}
                onChange={handleInputChange}
                placeholder="Share your feedback, suggestions, or comments..."
                required
              />
            </Form.Group>

            <div className="alert alert-info mb-0">
              <i className="fas fa-lock me-2"></i>
              <small>Your feedback is confidential and will be used to improve our services.</small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={createFeedbackMutation.isPending}
            >
              {createFeedbackMutation.isPending ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-2"></i>
                  Submit Feedback
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View Feedback Details Modal */}
      <Modal
        show={!!selectedFeedback}
        onHide={() => setSelectedFeedback(null)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-comment-dots me-2"></i>
            Feedback Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFeedback && (
            <div>
              <div className="mb-3">
                <strong className="text-muted">Submitted on:</strong>
                <p>{formatDate(selectedFeedback.createdAt)}</p>
              </div>
              <div className="mb-3">
                <strong className="text-muted">Name:</strong>
                <p>{selectedFeedback.name}</p>
              </div>
              <div className="mb-3">
                <strong className="text-muted">Feedback:</strong>
                <div className="p-3 bg-light rounded mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedFeedback.feedback}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedFeedback(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

